import { useNotifications } from '../../data/notifications.js';

/**
 * WS-14 — the NOTIFICATIONS bell trigger. A button (mounted in BOTH the desktop rail Tools section and
 * the mobile topbar actions, beside Search/Help) that opens the inbox drawer and carries an unread-count
 * badge. The unread count is the salon-wide figure from the polled list (data/notifications.ts), so it
 * tracks new events without a manual refresh. Two visual variants share one count source:
 *   • variant="rail"   — the porcelain pill in the rail (matches the Search/Help triggers)
 *   • variant="topbar" — the bare icon button in the mobile topbar
 *
 * The accessible name folds the unread count in ("Notifications, 3 unread") so a screen-reader user
 * hears it without seeing the badge; the badge itself is aria-hidden (decorative duplication).
 */
export function NotificationsBell({
  onOpen,
  variant,
  buttonRef,
}: {
  onOpen: () => void;
  variant: 'rail' | 'topbar';
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  // The bell polls the unread-only count cheaply (the drawer owns the full list). A salon with no
  // notifications yet just shows no badge.
  const { data } = useNotifications();
  const unread = data?.unreadCount ?? 0;
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';
  const badge = unread > 99 ? '99+' : String(unread);

  if (variant === 'topbar') {
    return (
      <button
        ref={buttonRef}
        type="button"
        className="app-topbar__icon notif-bell notif-bell--topbar"
        aria-label={label}
        aria-haspopup="dialog"
        onClick={onOpen}
      >
        <span aria-hidden>🔔</span>
        {unread > 0 ? (
          <span className="notif-bell__badge" aria-hidden>
            {badge}
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      className="app-help-trigger notif-bell notif-bell--rail"
      aria-label={label}
      aria-haspopup="dialog"
      onClick={onOpen}
    >
      <span aria-hidden>🔔</span>
      {unread > 0 ? (
        <span className="notif-bell__badge" aria-hidden>
          {badge}
        </span>
      ) : null}
    </button>
  );
}
